import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { Book, Image, Quote, Target, Star, Edit, Settings, ChevronDown, ChevronUp, Plus, Check, X, GripVertical, Paperclip, Download, Presentation, Trash2, Type, Eye, EyeOff } from 'lucide-react';
import { useSectionStyles } from '../hooks/useSectionStyles';
import tippy from 'tippy.js';
import { ReactRenderer } from '@tiptap/react';
import { BibleSelector } from '../components/BibleSelector';
import { MediaUploader } from './MediaUploader';
import { MediaDownloader } from './MediaDownloader';
import { useSermonMedia } from '../hooks/useSermonMedia';

interface SermonSectionProps {
  node: {
    attrs: {
      type: string;
      pointNumber?: string;
      attribution?: string;
      isSlide?: boolean;
      id?: string;
      customTitle?: string;
    };
  };
  editor: any;
  getPos: () => number;
  updateAttributes: (attrs: Record<string, any>) => void;
}

export function SermonSectionComponent({ node, editor, getPos, updateAttributes }: SermonSectionProps) {
  const { styles } = useSectionStyles();
  const type = node.attrs.type || 'scripture';
  // Auto-calculate point number if not manually set
  const getAutoPointNumber = () => {
    if (type !== 'main-point') return '';

    // Get all main-point sections before this one
    const currentPos = getPos();
    const doc = editor.state.doc;
    let pointCount = 0;

    doc.descendants((node, pos) => {
      // Only count main-point sections that come before this position
      if (pos < currentPos &&
          node.type.name === 'sermonSection' &&
          node.attrs.type === 'main-point') {
        pointCount++;
      }
      return true;
    });

    return String(pointCount + 1);
  };

  const [pointInput, setPointInput] = useState(node.attrs.pointNumber || '');
  const [isEditingPoint, setIsEditingPoint] = useState(false);
  const pointInputRef = useRef<HTMLInputElement>(null);
  const [attribution, setAttribution] = useState(node.attrs.attribution || '');
  const [isEditingAttribution, setIsEditingAttribution] = useState(false);
  const attributionInputRef = useRef<HTMLInputElement>(null);
  const [isSlide, setIsSlide] = useState(node.attrs.isSlide || false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showMediaUploader, setShowMediaUploader] = useState(false);
  const [showMediaDownloader, setShowMediaDownloader] = useState(false);
  const [mediaCount, setMediaCount] = useState(0);
  const { loadSectionMedia } = useSermonMedia();
  const [showSettings, setShowSettings] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tempPointNumber, setTempPointNumber] = useState(''); // Initialize with empty string
  const [tempAttribution, setTempAttribution] = useState(node.attrs.attribution || '');
  // For non-custom sections, customTitle can be empty/null (use label as fallback)
  // For custom sections, default to 'Custom Section' if not provided
  const [customTitle, setCustomTitle] = useState(
    node.attrs.customTitle || (type === 'custom' ? 'Custom Section' : '')
  );
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isVisible, setIsVisible] = useState(() => {
    // Load visibility state from localStorage
    const sectionId = node.attrs.id || String(getPos());
    const sermonId = editor.storage.sermonId;
    if (sermonId && sectionId) {
      const savedState = localStorage.getItem(`sermon-section-visibility-${sermonId}-${sectionId}`);
      return savedState !== null ? JSON.parse(savedState) : true;
    }
    return true;
  });

  // Get the display number (manual override or auto-calculated)
  const displayPointNumber = node.attrs.pointNumber || getAutoPointNumber();

  // Initialize tempPointNumber after displayPointNumber is available
  useEffect(() => {
    setTempPointNumber(displayPointNumber);
  }, [displayPointNumber]);

  // Save visibility state to localStorage
  useEffect(() => {
    const sectionId = node.attrs.id || String(getPos());
    const sermonId = editor.storage.sermonId;
    if (sermonId && sectionId) {
      localStorage.setItem(`sermon-section-visibility-${sermonId}-${sectionId}`, JSON.stringify(isVisible));
    }
  }, [isVisible, node.attrs.id, getPos, editor.storage.sermonId]);


  const sectionIcons = {
    'scripture': Book,
    'illustration': Image,
    'quote': Quote,
    'application': Target,
    'main-point': Star,
    'custom': Type,
  };

  const Icon = sectionIcons[type as keyof typeof sectionIcons] || Book;
  const style = styles.find(s => s.type === type) || styles[0];
  const label = type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  // Focus input when editing point
  useEffect(() => {
    if (isEditingPoint && pointInputRef.current) {
      pointInputRef.current.focus();
    }
  }, [isEditingPoint]);

  // Focus input when editing attribution
  useEffect(() => {
    if (isEditingAttribution && attributionInputRef.current) {
      attributionInputRef.current.focus();
    }
  }, [isEditingAttribution]);

  // Load media counts for this section
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second

    const getMediaCount = async () => {
      // Extract sermon ID from the editor state
      const sermonId = editor.storage.sermonId;
      if (!sermonId) return;

      const sectionId = node.attrs.id || String(getPos());

      try {
        const sectionMedia = await loadSectionMedia(sermonId, sectionId);
        if (isMounted) {
          setMediaCount(sectionMedia.length);
        }
      } catch (error) {
        console.error('Error loading section media:', error);
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          setTimeout(getMediaCount, RETRY_DELAY * retryCount);
        }
      }
    };

    getMediaCount();

    return () => {
      isMounted = false;
    };
  }, [editor, node.attrs, getPos, loadSectionMedia, showMediaUploader]);

  const getSectionTitle = () => {
    switch (type) {
      case 'scripture': return 'Scripture';
      case 'illustration': return 'Illustration';
      case 'quote': return 'Quote';
      case 'application': return 'Application';
      case 'main-point': return `Main Point${displayPointNumber ? ` ${displayPointNumber}` : ''}`;
      case 'custom': return customTitle;
      default: return 'Section';
    }
  };

  const handleCustomTitleClick = (e: React.MouseEvent) => {
    if (type === 'custom') {
      e.preventDefault();
      e.stopPropagation();
      setIsEditingTitle(true);
    }
  };

  const handleTitleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTitle(e.target.value);
  };

  const handleTitleInputBlur = () => {
    handleTitleSave();
  };

  const handleTitleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setCustomTitle(node.attrs.customTitle || 'Custom Section');
      setIsEditingTitle(false);
    }
  };

  const handleSectionClick = (e: React.MouseEvent) => {
    if (type === 'scripture') {
      e.preventDefault();
      e.stopPropagation();

      const pos = getPos();

      let bibleSelector: any;
      let popup: any;

      const onSelect = (passage: any) => {
        // Get the section node
        const node = editor.state.doc.nodeAt(pos);
        if (!node) return;

        // Create the passage text with proper formatting
        const passageText = [
          `${passage.reference} (${passage.version})`,
          '',  // Empty line for spacing
          passage.text
        ].join('\n');

        // Replace the section content using a paragraph node
        editor
          .chain()
          .focus()
          .command(({ tr, dispatch }) => {
            const from = pos;
            const to = pos + node.nodeSize;

            // Create a new section with properly structured content
            const paragraphNode = editor.schema.nodes.paragraph.create(
              {},
              editor.schema.text(passageText)
            );

            const newNode = editor.schema.nodes.sermonSection.create(
              { type: 'scripture' },
              paragraphNode
            );

            tr.replaceWith(from, to, newNode);
            return true;
          })
          .run();

        // Clean up the popup and selector
        if (popup && popup[0]) {
          popup[0].destroy();
        }
        if (bibleSelector) {
          bibleSelector.destroy();
        }
      };

      const onClose = () => {
        if (popup && popup[0]) {
          popup[0].destroy();
        }
        if (bibleSelector) {
          bibleSelector.destroy();
        }
      };

      bibleSelector = new ReactRenderer(BibleSelector, {
        props: {
          onSelect,
          onClose,
        },
        editor,
      });

      const coords = editor.view.coordsAtPos(pos);
      const domRect = new DOMRect(
        coords.left,
        coords.bottom,
        0,
        0
      );

      popup = tippy('body', {
        getReferenceClientRect: () => domRect,
        appendTo: () => document.body,
        content: bibleSelector.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        theme: 'light',
        arrow: true,
        zIndex: 100000,
      });
    } else if (type === 'main-point') {
      e.preventDefault();
      e.stopPropagation();
      setIsEditingPoint(true);
    } else if (type === 'quote') {
      e.preventDefault();
      e.stopPropagation();
      setIsEditingAttribution(true);
    }
  };

  const handlePointInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPointInput(e.target.value);
  };

  const handlePointInputBlur = () => {
    updatePointNumber(pointInput);
    setIsEditingPoint(false);
  };

  const handlePointInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      updatePointNumber(pointInput);
      setIsEditingPoint(false);
    } else if (e.key === 'Escape') {
      setPointInput(node.attrs.pointNumber || '');
      setIsEditingPoint(false);
    }
  };

  const handleAttributionInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAttribution(e.target.value);
  };

  const handleAttributionInputBlur = () => {
    updateAttribution(attribution);
    setIsEditingAttribution(false);
  };

  const handleAttributionInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      updateAttribution(attribution);
      setIsEditingAttribution(false);
    } else if (e.key === 'Escape') {
      setAttribution(node.attrs.attribution || '');
      setIsEditingAttribution(false);
    }
  };

  const updatePointNumber = (value: string) => {
    updateAttributes({ pointNumber: value });
  };

  const updateAttribution = (value: string) => {
    updateAttributes({ attribution: value });
  };

  const toggleSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newIsSlide = !isSlide;
    setIsSlide(newIsSlide);
    updateAttributes({ isSlide: newIsSlide });
  };

  const handleMediaClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Assign a stable section ID if not already set
    if (!node.attrs.id) {
      updateAttributes({ id: String(getPos()) });
    }

    setShowMediaUploader(true);
  };

  const handleMediaDownload = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Ensure we have a section ID
    const sectionId = node.attrs.id || String(getPos());

    // Get sermon ID from editor storage
    const sermonId = editor.storage.sermonId;
    if (!sermonId) {
      console.error('No sermon ID found in editor storage');
      return;
    }

    setShowMediaDownloader(true);
  };

  const handleDeleteSection = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (window.confirm('Are you sure you want to delete this section?')) {
      const pos = getPos();
      const sectionNode = editor.state.doc.nodeAt(pos);

      if (!sectionNode) return;

      // Delete the entire section and replace with a paragraph
      const newParagraph = editor.schema.nodes.paragraph.create();
      editor
        .chain()
        .focus()
        .command(({ tr, dispatch }) => {
          if (dispatch) {
            tr.replaceWith(pos, pos + sectionNode.nodeSize, newParagraph);

            // Move cursor to the new paragraph
            const resolvedPos = tr.doc.resolve(pos + 1);
            tr.setSelection(editor.state.selection.constructor.near(resolvedPos));
          }
          return true;
        })
        .run();
    }
  };

  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const handleExitSection = (e: React.MouseEvent) => {
    e.stopPropagation();
    const pos = getPos();
    const sectionNode = editor.state.doc.nodeAt(pos);
    if (!sectionNode) { editor.commands.blur(); return; }
    const afterPos = Math.min(pos + sectionNode.nodeSize, editor.state.doc.content.size);
    editor.commands.focus(afterPos);
  };

  const handleSave = () => {
    updateAttributes({
      pointNumber: tempPointNumber,
      attribution: tempAttribution,
      customTitle: customTitle
    });
    setIsEditing(false);
  };

  const handleTitleSave = () => {
    updateAttributes({
      customTitle: customTitle
    });
    setIsEditingTitle(false);
  };

  const toggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  // Get the CSS variables for this section type
  const bgColorVar = `var(--${style.backgroundColor.replace('bg-', '').split('/')[0]}-bg)`;
  const textColorVar = `var(--${style.textColor.replace('text-', '')})`;
  const borderColorVar = `var(--${style.borderColor.replace('border-', '')})`;

  return (
    <NodeViewWrapper
      className={`sermon-section sermon-section-${type} ${isDragging ? 'dragging' : ''}`}
      data-point-number={node.attrs.pointNumber || ''}
      data-attribution={node.attrs.attribution || ''}
      data-is-slide={isSlide ? 'true' : 'false'}
      data-section-id={node.attrs.id || String(getPos())}
      data-visible={isVisible ? 'true' : 'false'}
    >
      <div
        ref={sectionRef}
        className={`my-4 rounded-lg overflow-hidden ${!isVisible ? 'opacity-60 border-2 border-dashed border-slate/30' : ''}`}
        style={{
          backgroundColor: bgColorVar,
          borderLeftWidth: '4px',
          borderLeftStyle: 'solid',
          borderLeftColor: borderColorVar
        }}
      >
        <div
          className="section-header w-full flex items-center justify-between gap-2 px-6 pt-6 pb-2 hover:bg-black/5 transition-colors cursor-pointer text-left"
          onClick={handleSectionClick}
          style={{ color: textColorVar }}
        >
          <div className="flex items-center gap-2">
            <div
              ref={dragHandleRef}
              className="drag-handle flex items-center justify-center rounded hover:bg-white/20 active:bg-white/30 transition-colors min-w-[44px] min-h-[44px]"
              data-drag-handle
              style={{ touchAction: 'none' }}
            >
              <GripVertical className="h-5 w-5" />
            </div>
            <Icon className="h-4 w-4" />
            {type === 'custom' ? (
              isEditingTitle ? (
                <div onClick={(e) => e.stopPropagation()} className="ml-2">
                  <input
                    type="text"
                    value={customTitle}
                    onChange={handleTitleInputChange}
                    onBlur={handleTitleInputBlur}
                    onKeyDown={handleTitleInputKeyDown}
                    className="px-2 py-1 text-sm bg-white/80 border border-white/50 rounded focus:outline-none focus:ring-1 focus:ring-white"
                    placeholder="Enter section title..."
                    autoFocus
                  />
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="section-type-label">{customTitle}</span>
                  <button
                    onClick={handleCustomTitleClick}
                    className="p-1 rounded hover:bg-white/20 transition-colors"
                    title="Edit title"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                </div>
              )
            ) : (
              // Use customTitle if available (for translated sections), otherwise use English label
              <span className="section-type-label">{customTitle || label}</span>
            )}
            {type === 'main-point' && (
              isEditingPoint ? (
                <div onClick={(e) => e.stopPropagation()} className="ml-2">
                  <input
                    ref={pointInputRef}
                    type="text"
                    value={pointInput}
                    onChange={handlePointInputChange}
                    onBlur={handlePointInputBlur}
                    onKeyDown={handlePointInputKeyDown}
                    className="w-16 px-2 py-1 text-sm bg-white/80 border border-white/50 rounded focus:outline-none focus:ring-1 focus:ring-white"
                    placeholder="1, A, I..."
                    maxLength={5}
                  />
                </div>
              ) : (
                <div className="ml-2 px-3 py-1 bg-white/40 rounded-lg text-lg font-black min-w-[32px] min-h-[32px] flex items-center justify-center shadow-sm">
                  {displayPointNumber}
                </div>
              )
            )}
          </div>
          <div className="flex items-center">
            {type === 'quote' && (
              isEditingAttribution ? (
                <div onClick={(e) => e.stopPropagation()} className="ml-auto">
                  <input
                    ref={attributionInputRef}
                    type="text"
                    value={attribution}
                    onChange={handleAttributionInputChange}
                    onBlur={handleAttributionInputBlur}
                    onKeyDown={handleAttributionInputKeyDown}
                    className="w-64 px-2 py-1 text-sm bg-white/80 border border-white/50 rounded focus:outline-none focus:ring-1 focus:ring-white"
                    placeholder="Author or source..."
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsEditingAttribution(true); }}
                  className="ml-auto italic text-sm opacity-80 min-w-[44px] min-h-[44px] flex items-center cursor-pointer"
                  style={{ touchAction: 'manipulation' }}
                >
                  {node.attrs.attribution ? `— ${node.attrs.attribution}` : "— ?"}
                </button>
              )
            )}
            <button
              onClick={handleMediaClick}
              className={`ml-2 p-1 rounded relative ${mediaCount > 0 ? 'bg-white/30' : 'bg-transparent hover:bg-white/10'}`}
              title="Attach Media"
              type="button"
            >
              <Paperclip className={`h-4 w-4 ${mediaCount > 0 ? 'text-white' : 'opacity-70 hover:opacity-100'}`} />
              {mediaCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-crimson text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {mediaCount}
                </span>
              )}
            </button>
            <button
              onClick={handleMediaDownload}
              className={`ml-2 p-1 rounded ${mediaCount > 0 ? 'bg-white/30' : 'bg-transparent hover:bg-white/10'}`}
              title="Download Media"
              type="button"
              disabled={mediaCount === 0}
            >
              <Download className={`h-4 w-4 ${mediaCount > 0 ? 'text-white' : 'opacity-70 hover:opacity-100'}`} />
            </button>
            <button
              onClick={toggleSlide}
              className={`ml-2 p-1 rounded ${isSlide ? 'bg-white/30' : 'bg-transparent hover:bg-white/10'}`}
              title={isSlide ? "Remove from slides" : "Add to slides"}
              type="button"
            >
              <Presentation className={`h-4 w-4 ${isSlide ? 'text-white' : 'opacity-70 hover:opacity-100'}`} />
            </button>
            <button
              onClick={toggleVisibility}
              className={`ml-2 p-1 rounded ${!isVisible ? 'bg-white/30' : 'bg-transparent hover:bg-white/10'}`}
              title={isVisible ? "Hide from preach mode" : "Show in preach mode"}
              type="button"
            >
              {isVisible ? (
                <Eye className="h-4 w-4 opacity-70 hover:opacity-100" />
              ) : (
                <EyeOff className="h-4 w-4 text-white" />
              )}
            </button>
            <button
              onClick={handleDeleteSection}
              className="ml-2 p-1 rounded bg-transparent hover:bg-white/10"
              title="Delete section"
              type="button"
            >
              <Trash2 className="h-4 w-4 opacity-70 hover:opacity-100" />
            </button>
          </div>
        </div>

        <div
          className="px-6 pb-6 relative"
          style={{ color: textColorVar }}
        >
          {type === 'main-point' && (
            <div
              className="absolute -top-8 right-6 opacity-10 pointer-events-none select-none"
              style={{ fontSize: '8rem', lineHeight: '1', fontWeight: '900' }}
            >
              {displayPointNumber}
            </div>
          )}
          <div className="relative z-10">
            <NodeViewContent />
          </div>
          {isTouchDevice && (
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleExitSection}
                className="text-xs px-3 py-1.5 rounded-full bg-white/30 hover:bg-white/50 transition-colors font-medium"
                style={{ touchAction: 'manipulation' }}
              >
                Done ↓
              </button>
            </div>
          )}
        </div>
      </div>

      {showMediaUploader && (
        <MediaUploader
          sermonId={editor.storage.sermonId}
          sectionId={node.attrs.id || String(getPos())}
          onClose={() => setShowMediaUploader(false)}
        />
      )}

      {showMediaDownloader && (
        <MediaDownloader
          sermonId={editor.storage.sermonId}
          sectionId={node.attrs.id || String(getPos())}
          onClose={() => setShowMediaDownloader(false)}
        />
      )}
    </NodeViewWrapper>
  );
}